import config from 'constants/config'
import getPaymentMethods from './getPaymentMethods'
import {
  KLARNA_PAYMENT_TYPE,
  MIN_KLARNA_PAYMENT,
  MAX_KLARNA_PAYMENT,
} from 'constants/payment'

export const isKlarnaEnabled = (currency: string): boolean =>
  !!(config.KLARNA_ENABLED && getPaymentMethods(currency).includes(KLARNA_PAYMENT_TYPE))

export const isWithinKlarnaAvailabilityRange = (amount: number): boolean =>
  amount >= MIN_KLARNA_PAYMENT && amount <= MAX_KLARNA_PAYMENT

/**
 * Extended Merchant Data Payload Schema
 *
 * Created from the JSONSchema at https://developers.klarna.com/api/specs/attachment-schema.json
 * Converted to TS type with http://borischerny.com/json-schema-to-typescript-browser/
 */
export interface KlarnaAttachment {
  /**
   * Details about the reservation of airline tickets
   */
  air_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Itinerary data, one per segment
     */
    itinerary?: {
      /**
       * IATA Airport Code (three letters)
       */
      departure?: string;
      departure_city?: string;
      /**
       * IATA Airport Code (three letters)
       */
      arrival?: string;
      arrival_city?: string;
      /**
       * IATA Airline standard (two letters or digits)
       */
      carrier?: string;
      /**
       * Local currency
       */
      segment_price?: number;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      departure_date?: string;
      ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
      /**
       * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
       */
      ticket_delivery_recipient?: string;
      passenger_id?: number[];
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    /**
     * Passengers data
     */
    passengers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  /**
   * Details about the reservation of bus tickets
   */
  bus_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Itinerary data, one per segment
     */
    itinerary?: {
      /**
       * City code or name
       */
      departure?: string;
      departure_city?: string;
      /**
       * City code or name
       */
      arrival?: string;
      arrival_city?: string;
      /**
       * Name of transportation company
       */
      carrier?: string;
      /**
       * Local currency
       */
      segment_price?: number;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      departure_date?: string;
      ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
      /**
       * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
       */
      ticket_delivery_recipient?: string;
      passenger_id?: number[];
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    /**
     * Passengers data
     */
    passengers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  /**
   * Details about the reservation of train tickets
   */
  train_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Itinerary data, one per segment
     */
    itinerary?: {
      /**
       * City code or name
       */
      departure?: string;
      departure_city?: string;
      /**
       * City code or name
       */
      arrival?: string;
      arrival_city?: string;
      /**
       * Name of transportation company
       */
      carrier?: string;
      /**
       * Local currency
       */
      segment_price?: number;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      departure_date?: string;
      ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
      /**
       * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
       */
      ticket_delivery_recipient?: string;
      passenger_id?: number[];
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    /**
     * Passengers data
     */
    passengers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  /**
   * Details about the reservation of ferry tickets
   */
  ferry_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Itinerary data, one per segment
     */
    itinerary?: {
      /**
       * City code or name
       */
      departure?: string;
      departure_city?: string;
      /**
       * City code or name
       */
      arrival?: string;
      arrival_city?: string;
      /**
       * Name of transportation company
       */
      carrier?: string;
      /**
       * Local currency
       */
      segment_price?: number;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      departure_date?: string;
      ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
      /**
       * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
       */
      ticket_delivery_recipient?: string;
      passenger_id?: number[];
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    /**
     * Passengers data
     */
    passengers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  /**
   * Details about the reservation of hotel rooms
   */
  hotel_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Hotel itinerary data, one per hotel stay
     */
    hotel_itinerary?: {
      /**
       * Name of hotel
       */
      hotel_name?: string;
      address?: {
        street_address?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        [k: string]: unknown;
      };
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      start_time?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      end_time?: string;
      number_of_rooms?: number;
      passenger_id?: number[];
      ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
      /**
       * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
       */
      ticket_delivery_recipient?: string;
      /**
       * Local currency
       */
      hotel_price?: number;
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    /**
     * Passengers data
     */
    passengers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  /**
   * Details about the reservation of rental cars
   */
  car_rental_reservation_details?: {
    /**
     * Trip booking number, e.g. VH67899
     */
    pnr?: string;
    /**
     * Car rental itinerary data
     */
    car_rental_itinerary?: {
      /**
       * Name of rental company
       */
      rental_company?: string;
      drivers_id?: number[];
      pick_up_location?: {
        street_address?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        [k: string]: unknown;
      };
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      start_time?: string;
      drop_off_location?: {
        street_address?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        [k: string]: unknown;
      };
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      end_time?: string;
      /**
       * Local currency
       */
      car_price?: number;
      class?: string;
      [k: string]: unknown;
    }[];
    /**
     * Insurance data
     */
    insurance?: {
      insurance_company?: string;
      insurance_type?: 'cancellation' | 'travel' | 'cancellation_travel' | 'bankruptcy';
      /**
       * Local currency
       */
      insurance_price?: number;
      [k: string]: unknown;
    }[];
    drivers?: {
      id?: number;
      /**
       * Mr, Mrs, Ms or blank if under 12 years
       */
      title?: 'mr' | 'mrs' | 'ms' | '';
      first_name?: string;
      last_name?: string;
      [k: string]: unknown;
    }[];
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  event?: {
    /**
     * Name of the event, e.g. "Fancy Singer"
     */
    event_name?: string;
    /**
     * Name of the company arranging the event, e.g. "Happy Parties Ltd."
     */
    event_company?: string;
    /**
     * Category or type of venue, "Pop"
     */
    genre_of_event?: string;
    /**
     * Name of the venue, "Song Arena"
     */
    arena_name?: string;
    /**
     * Location of the arena.
     */
    arena_location?: {
      street_address?: string;
      postal_code?: string;
      city?: string;
      country?: string;
      [k: string]: unknown;
    };
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    start_time?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    end_time?: string;
    /**
     * Tickets are digitally checked when enetering the venue
     */
    access_controlled_venue?: boolean;
    ticket_delivery_method?: 'pick_up' | 'email' | 'post' | 'phone';
    /**
     * The name of the recipient the ticket is delivered to. If email or phone, then use either the email address or the phone number.
     */
    ticket_delivery_recipient?: string;
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  voucher?: {
    /**
     * Description of the product the voucher entitles to, e.g. "China trip"
     */
    voucher_name?: string;
    /**
     * Name of the company that will provide good / service upon voucher (not the same as the selling merchant), e.g. "Sun Trips Ltd."
     */
    voucher_company?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    start_time?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    end_time?: string;
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  subscription?: {
    /**
     * Name of the product on subscription, e.g. "Contact lenses"
     */
    subscription_name?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    start_time?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    end_time?: string;
    /**
     * Info whether the subscription will be auto renewed upon expiry.
     */
    auto_renewal_of_subscription?: boolean;
    /**
     * Name of the affiliate that originated the purchase. If none, leave blank.
     */
    affiliate_name?: string;
    [k: string]: unknown;
  }[];
  marketplace_seller_info?: {
    unique_account_identifier_seller?: {
      email?: string;
      pno?: string;
      other?: string;
      [k: string]: unknown;
    };
    /**
     * Name or unique number of the selling / delivering merchant, e.g. "Marketbrick Ltd."
     */
    sub_merchant_id?: string;
    /**
     * Name of the overall category to which the specific good(s) belong to, according to the selling merchants categorization. E.g. "Computers"
     */
    product_category?: string;
    /**
     * Name of the specific good purchased, e.g. "Acer 5400"
     */
    product_name?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    account_registration_date?: string;
    account_last_modified?: {
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      password?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      email?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      listing?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      login?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      address?: string;
      [k: string]: unknown;
    };
    /**
     * Current rating. Partner should provide upfront their rating scale, e.g. 65
     */
    seller_rating?: number;
    /**
     * Number of trades the sub-merchant did since 12 months back, e.g. 23
     */
    number_of_trades?: number;
    /**
     * Volumes of trades the sub-merchant did since 12 months back, e.g. 230.5
     */
    volume_of_trades?: number;
    [k: string]: unknown;
  }[];
  marketplace_winner_info?: {
    unique_account_identifier_winner?: {
      email?: string;
      pno?: string;
      other?: string;
      [k: string]: unknown;
    };
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    account_registration_date?: string;
    account_last_modified?: {
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      password?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      email?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      listing?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      login?: string;
      /**
       * ISO 8601 e.g. 2012-11-24T15:00
       */
      address?: string;
      [k: string]: unknown;
    };
    /**
     * Number of trades the winner did since 12 months back, e.g. 5
     */
    number_of_trades?: number;
    /**
     * Volume of trades the winner did since 12 months back, 230.5
     */
    volume_of_trades?: number;
    [k: string]: unknown;
  }[];
  customer_account_info?: {
    /**
     * Unique name / number to identify the specific customer account. Max 24 characters. E.g. "Adam Adamsson"
     */
    unique_account_identifier?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    account_registration_date?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    account_last_modified?: string;
    [k: string]: unknown;
  }[];
  payment_history_full?: {
    /**
     * Unique name / number to identify the specific customer account. Max 24 characters. E.g. "Adam Adamsson"
     */
    unique_account_identifier?: string;
    payment_option?: 'card' | 'direct banking' | 'non klarna credit' | 'sms' | 'other';
    /**
     * Total number of successful purchases
     */
    number_paid_purchases?: number;
    /**
     * Total amount of successful purchases (local currency), e.g. 1234.00
     */
    total_amount_paid_purchases?: number;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    date_of_last_paid_purchase?: string;
    /**
     * ISO 8601 e.g. 2012-11-24T15:00
     */
    date_of_first_paid_purchase?: string;
  }[];
  payment_history_simple?: {
    /**
     * Unique name / number to identify the specific customer account. Max 24 characters. E.g. "Adam Adamsson"
     */
    unique_account_identifier?: string;
    /**
     * Whether the customer has paid before or not
     */
    paid_before?: boolean;
  }[];
  other_delivery_address?: {
    shipping_method?: 'store pick-up' | 'pick-up point' | 'registered box' | 'unregistered box';
    shipping_type?: 'normal' | 'express';
    first_name?: string;
    last_name?: string;
    street_address?: string;
    street_number?: string;
    postal_code?: string;
    city?: string;
    country?: string;
  }[];
  [k: string]: unknown;
}
